import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react'
import { useLanguage } from '../../context/LanguageContext'

const GRID_SPANS = [
  'sm:col-span-2 sm:row-span-2',
  '',
  '',
  'sm:col-span-2',
  '',
  '',
  'sm:row-span-2',
  '',
  '',
  '',
]

export default function CampusPhotoGallery({ images, label, title, subtitle }) {
  const { t } = useTranslation()
  const { isRTL } = useLanguage()
  const [lightboxIndex, setLightboxIndex] = useState(null)

  const openLightbox = (index) => setLightboxIndex(index)
  const closeLightbox = () => setLightboxIndex(null)

  const showPrev = useCallback(() => {
    setLightboxIndex((current) =>
      current === null ? null : (current - 1 + images.length) % images.length
    )
  }, [images.length])

  const showNext = useCallback(() => {
    setLightboxIndex((current) =>
      current === null ? null : (current + 1) % images.length
    )
  }, [images.length])

  useEffect(() => {
    if (lightboxIndex === null) return undefined

    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeLightbox()
      if (event.key === 'ArrowLeft') (isRTL ? showNext : showPrev)()
      if (event.key === 'ArrowRight') (isRTL ? showPrev : showNext)()
    }

    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKeyDown)

    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [lightboxIndex, isRTL, showNext, showPrev])

  if (!images?.length) return null

  const activeImage = lightboxIndex !== null ? images[lightboxIndex] : null

  return (
    <section className="my-14">
      <div className="mb-8 text-center">
        {label && (
          <p className="mb-2 text-sm font-bold uppercase tracking-widest text-accent">{label}</p>
        )}
        <h2 className="text-2xl font-bold text-primary sm:text-3xl">{title}</h2>
        {subtitle && (
          <p className="mx-auto mt-3 max-w-2xl text-base leading-relaxed text-text/65">{subtitle}</p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:auto-rows-[180px] lg:auto-rows-[220px]">
        {images.map((image, index) => (
          <button
            key={image.src}
            type="button"
            onClick={() => openLightbox(index)}
            className={`group relative overflow-hidden rounded-2xl bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${GRID_SPANS[index] || ''}`}
          >
            <img
              src={image.src}
              alt={image.alt || `${title} ${index + 1}`}
              className="h-full min-h-[180px] w-full object-cover transition-transform duration-700 group-hover:scale-105 sm:min-h-0"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/70 via-primary/10 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            <div className="absolute inset-0 flex items-end justify-between p-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white backdrop-blur-sm">
                {index + 1} / {images.length}
              </span>
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-primary shadow-lg">
                <ZoomIn className="h-5 w-5" />
              </span>
            </div>
          </button>
        ))}
      </div>

      <AnimatePresence>
        {activeImage && lightboxIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/92 p-4 backdrop-blur-sm"
            onClick={closeLightbox}
          >
            <button
              type="button"
              onClick={closeLightbox}
              className="absolute end-4 top-4 z-10 rounded-full bg-white/10 p-2.5 text-white transition-colors hover:bg-white/20"
              aria-label={t('pagesExtras.campusGallery.close')}
            >
              <X className="h-6 w-6" />
            </button>

            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                showPrev()
              }}
              className="absolute start-3 top-1/2 z-10 hidden -translate-y-1/2 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-white/20 sm:block"
              aria-label={t('pagesExtras.campusGallery.previous')}
            >
              <ChevronLeft className={`h-6 w-6 ${isRTL ? 'rotate-180' : ''}`} />
            </button>

            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                showNext()
              }}
              className="absolute end-3 top-1/2 z-10 hidden -translate-y-1/2 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-white/20 sm:block"
              aria-label={t('pagesExtras.campusGallery.next')}
            >
              <ChevronRight className={`h-6 w-6 ${isRTL ? 'rotate-180' : ''}`} />
            </button>

            <motion.figure
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="relative max-h-[88vh] max-w-6xl"
              onClick={(event) => event.stopPropagation()}
            >
              <img
                src={activeImage.src}
                alt={activeImage.alt || `${title} ${lightboxIndex + 1}`}
                className="max-h-[82vh] w-full rounded-xl object-contain shadow-2xl"
              />
              <figcaption className="mt-4 flex items-center justify-between gap-4 text-sm text-white/75">
                <span>{activeImage.alt || title}</span>
                <span className="font-semibold text-white">
                  {lightboxIndex + 1} / {images.length}
                </span>
              </figcaption>
            </motion.figure>

            <div className="absolute bottom-4 flex gap-2 sm:hidden">
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation()
                  showPrev()
                }}
                className="rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white"
              >
                {t('pagesExtras.campusGallery.previous')}
              </button>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation()
                  showNext()
                }}
                className="rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white"
              >
                {t('pagesExtras.campusGallery.next')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}
